<template>
  <div class="service-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('services.edit_title') : $t('services.create_title') }}</h2>
      </template>

      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group id="title-group" :label="$t('services.form.title_label')" label-for="title"
              :state="!hasError('title')" :invalid-feedback="getError('title')">
              <b-form-input id="title" v-model="formFields.title" :state="!hasError('title')" required
                @input="removeError('title')"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group id="price-group" :label="$t('services.form.price_label')" label-for="price"
              :state="!hasError('price')" :invalid-feedback="getError('price')">
              <b-form-input id="price" v-model="formFields.price" type="number" min="0" step="0.01"
                :state="!hasError('price')" required @input="removeError('price')"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group id="sort_description-group" :label="$t('services.form.sort_description_label')"
              label-for="sort_description" :state="!hasError('sort_description')"
              :invalid-feedback="getError('sort_description')">
              <b-form-textarea id="sort_description" v-model="formFields.sort_description" rows="3"
                :state="!hasError('sort_description')" required
                @input="removeError('sort_description')"></b-form-textarea>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group id="long_description-group" :label="$t('services.form.long_description_label')"
              label-for="long_description" :state="!hasError('long_description')"
              :invalid-feedback="getError('long_description')">
              <b-form-textarea id="long_description" v-model="formFields.long_description" rows="6"
                :state="!hasError('long_description')" required
                @input="removeError('long_description')"></b-form-textarea>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group id="status-group" :label="$t('services.form.status_label')" label-for="status"
              :state="!hasError('status')" :invalid-feedback="getError('status')">
              <b-form-select id="status" v-model="formFields.status" :options="statusOptions"
                :state="!hasError('status')" required @input="removeError('status')"></b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group id="attachments-group" :label="$t('services.form.attachments_label')" label-for="attachments"
              :state="!hasError('attachments')" :invalid-feedback="getError('attachments')">
              <b-form-file id="attachments" v-model="formFields.attachments" multiple :state="!hasError('attachments')"
                required @input="handleFileInput" accept=".jpg,.jpeg,.png,.gif,.mp4,.avi,.wmv,.mov" plain></b-form-file>
            </b-form-group>

            <!-- Preview uploaded files -->
            <div v-if="previewFiles.length" class="mt-3">
              <h6>{{ $t('services.form.preview_files') }}</h6>
              <div class="d-flex flex-wrap gap-2">
                <div v-for="(file, index) in previewFiles" :key="index" class="preview-item">
                  <img v-if="isImage(file)" :src="file.preview" class="preview-image" />
                  <video v-else-if="isVideo(file)" :src="file.preview" class="preview-video" controls></video>
                  <div class="preview-name">{{ file.name }}</div>
                  <b-button variant="danger" size="sm" @click="removeFile(index)" class="mt-1">
                    <i class="fas fa-times"></i>
                  </b-button>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
            {{ isSubmitting ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>

        <!-- Form Level Error Message -->
        <div v-if="formLevelError" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ formLevelError }}
          </b-alert>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";

export default {
  name: 'ServiceForm',
  mixins: [NotifyMixin],
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      isSubmitting: false,
      validated: false,
      formErrors: {},
      formLevelError: '',
      formFields: {
        title: '',
        sort_description: '',
        long_description: '',
        price: 0,
        status: 1,
        attachments: []
      },
      attachments_from_backend: [],
      previewFiles: [],
      statusOptions: [
        { value: 1, text: this.$t('services.status_active') },
        { value: 0, text: this.$t('services.status_inactive') }
      ],
      validationMessages: {
        title: 'The service title is required.',
        sort_description: 'A short description is required.',
        long_description: 'A detailed description is required.',
        price: 'The service price is required.',
        attachments: 'At least one attachment is required.'
      }
    }
  },

  computed: {
    isEdit() {
      return !!this.id;
    }
  },

  methods: {
    hasError(field) {
      return !!this.formErrors[field];
    },

    getError(field) {
      return this.formErrors[field] ? this.formErrors[field][0] : '';
    },

    removeError(field) {
      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
      this.formLevelError = '';
    },

    isImage(file) {
      return file.type.startsWith('image/');
    },

    isVideo(file) {
      return file.type.startsWith('video/');
    },

    handleFileInput(files) {
      this.formFields.attachments = Array.from(files); // Store actual File objects
      this.updatePreviewFiles(); // Update previews
    },

    updatePreviewFiles() {
      this.previewFiles = []; // Clear current previews

      // Add existing files from backend to preview
      this.attachments_from_backend.forEach(attachment => {
        this.previewFiles.push({
          name: attachment.file_name || attachment.original_name || 'N/A',
          type: attachment.mime_type || 'application/octet-stream',
          preview: attachment.url || ''
        });
      });

      // Add newly selected files to preview
      Array.from(this.formFields.attachments).forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewFiles.push({
            name: file.name,
            type: file.type,
            preview: e.target.result
          });
        };
        reader.readAsDataURL(file);
      });
    },

    removeFile(index) {
      const fileToRemove = this.previewFiles[index];
      this.previewFiles.splice(index, 1); // Remove from preview

      // Check if it's an existing attachment from the backend
      const existingAttachmentIndex = this.attachments_from_backend.findIndex(
        att => att.url === fileToRemove.preview
      );
      if (existingAttachmentIndex !== -1) {
        this.attachments_from_backend.splice(existingAttachmentIndex, 1);
        // IMPORTANT: If your backend needs to know which existing attachments to delete,
        // you'd typically collect IDs here to send to the server.
        // For now, these are just removed from client-side display and internal list.
      } else {
        // It's a newly selected file
        const newAttachmentIndex = this.formFields.attachments.findIndex(
          f => f.name === fileToRemove.name && f.type === fileToRemove.type && f.preview === fileToRemove.preview
        );
        if (newAttachmentIndex !== -1) {
          this.formFields.attachments.splice(newAttachmentIndex, 1);
        }
      }
    },

    async fetchService() {
      try {
        const response = await request({
          method: 'get',
          url: `/services/${this.id}`
        });
        // Populate form fields
        this.formFields.title = response.data.title;
        this.formFields.sort_description = response.data.sort_description;
        this.formFields.long_description = response.data.long_description;
        this.formFields.price = response.data.price;
        this.formFields.status = Number(response.data.status); // Ensure status is a number

        // Store existing attachments separately
        this.attachments_from_backend = response.data.attachments || [];
        this.updatePreviewFiles(); // Populate preview with existing attachments

        // Ensure formFields.attachments is empty when editing, unless new files are selected
        this.formFields.attachments = [];

      } catch (error) {
        console.error('Error fetching service:', error);
        // Use optional chaining for error.response and its properties
        this.formLevelError = error.data?.message || error.message || this.$t('services.fetch_error');
        this.notifyError(this.formLevelError);
        this.$router.push('/admin/services');
      }
    },

    async handleSubmit() {
      try {
        this.validated = true;
        this.isSubmitting = true;
        this.formErrors = {};
        this.formLevelError = '';

        // Client-side validation:
        if (!this.formFields.title.trim()) {
          this.formErrors.title = [this.validationMessages.title];
          throw new Error('Validation failed');
        }

        if (!this.formFields.sort_description.trim()) {
          this.formErrors.sort_description = [this.validationMessages.sort_description];
          throw new Error('Validation failed');
        }

        if (!this.formFields.long_description.trim()) {
          this.formErrors.long_description = [this.validationMessages.long_description];
          throw new Error('Validation failed');
        }

        if (this.formFields.price === null || this.formFields.price < 0) {
          this.formErrors.price = [this.validationMessages.price];
          throw new Error('Validation failed');
        }

        // Attachments validation: Only required on create, and only for new files.
        if (!this.isEdit && (!this.formFields.attachments || this.formFields.attachments.length === 0)) {
          this.formErrors.attachments = [this.validationMessages.attachments];
          throw new Error('Validation failed');
        }

        const formData = new FormData();
        // Append all regular form fields
        formData.append('title', this.formFields.title);
        formData.append('sort_description', this.formFields.sort_description);
        formData.append('long_description', this.formFields.long_description);
        formData.append('price', this.formFields.price);
        formData.append('status', this.formFields.status ? 1 : 0); // Ensure boolean is sent as 0/1

        // Append newly selected attachments
        this.formFields.attachments.forEach(file => {
          formData.append('attachments[]', file);
        });

        // If editing, and the backend expects existing files to be managed (e.g., by ID to preserve them),
        // you would append those IDs here.
        // For now, existing files are implicitly handled by the backend if not replaced by new uploads.

        let method = 'POST'; // Default to POST for FormData submissions
        let url = '/services';
        if (this.isEdit) {
          url = `/services/${this.id}/update`; // Your custom update URL
          formData.append('_method', 'POST'); // Spoof PUT method for Laravel API resources via POST
        }

        await request({
          method: method,
          url: url,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.notifySuccess(
          this.isEdit ? this.$t('services.update_success') : this.$t('services.create_success')
        );
        this.$router.push('/admin/services');
      } catch (error) {
        console.error('Error in handleSubmit:', error);
        // Use optional chaining for error.response and its properties, fall back to error.message
        if (error.status === 422) {
          this.formErrors = error.data?.errors || {};
          this.formLevelError = error.data?.message || this.$t('services.validation_error');
        } else {
          this.formLevelError = error.message || this.$t('services.validation_error');
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    cancel() {
      this.$router.push('/admin/services');
    }
  },

  created() {
    if (this.isEdit) {
      this.fetchService();
    }
  }
}
</script>

<style scoped>
.service-form {
  padding: 20px;
}

.preview-item {
  position: relative;
  width: 150px;
  margin-bottom: 10px;
}

.preview-image,
.preview-video {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

.preview-name {
  font-size: 0.8rem;
  margin-top: 5px;
  word-break: break-all;
}
</style>